import { useState, useEffect, useCallback } from 'react';
import { Library, Trash2, Copy, Check, Globe, Lock, Loader2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyCastsPage() {
  const [casts, setCasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const fetchCasts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ai/my-casts`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setCasts(data.casts || []); }
    } catch (err) { toast.error('Failed to load casts'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCasts(); }, [fetchCasts]);

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/api/ai/my-casts/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { setCasts(prev => prev.filter(c => c.id !== id)); toast.success('Cast deleted'); }
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handlePublish = async (id) => {
    try {
      const res = await fetch(`${API}/api/ai/store/publish/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_credits: 5 }),
      });
      if (res.ok) {
        setCasts(prev => prev.map(c => c.id === id ? { ...c, is_public: true } : c));
        toast.success('Published to PI Store!');
      }
    } catch (err) { toast.error('Failed to publish'); }
  };

  const categories = [...new Set(casts.map(c => c.category).filter(Boolean))];
  const filtered = casts.filter(c => {
    if (filterCat && c.category !== filterCat) return false;
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.cast.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-casts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-space text-2xl md:text-3xl text-white mb-2">My Casts</h1>
          <p className="text-white/40 text-sm">{casts.length} saved cast{casts.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search casts..."
            className="w-full pl-9 pr-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
            data-testid="search-casts" />
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setFilterCat('')}
            className={`px-3 py-1.5 text-xs rounded-full border font-medium ${!filterCat ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08]'}`}>All</button>
          {categories.map(c => (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-3 py-1.5 text-xs rounded-full border font-medium capitalize ${filterCat === c ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08]'}`}>{c}</button>
          ))}
        </div>
      </div>

      {/* Cast List */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-[#0E1830] border border-white/5 rounded-2xl">
          <Library className="w-12 h-12 text-white/10 mb-4" />
          <p className="text-white/30 text-sm">{casts.length === 0 ? 'No casts saved yet' : 'No casts match your search'}</p>
          <p className="text-white/15 text-xs mt-1">Generate casts with PI Cast or PI Loop to build your library</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(cast => (
            <div key={cast.id} className="bg-[#0E1830] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`cast-${cast.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium text-sm truncate">{cast.title}</h3>
                    {cast.is_public ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-bold"><Globe className="w-2.5 h-2.5" /> Store</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/25 text-[9px] font-bold"><Lock className="w-2.5 h-2.5" /> Private</span>
                    )}
                  </div>
                  <p className="text-white/30 text-xs line-clamp-2">{cast.cast}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-white/20">
                    <span className="capitalize">{cast.target_ai}</span>
                    <span>&middot;</span>
                    <span className="capitalize">{cast.category}</span>
                    <span>&middot;</span>
                    <span>{cast.token_count} tokens</span>
                    <span>&middot;</span>
                    <span>{cast.source}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => handleCopy(cast.id, cast.cast)}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white transition-colors" data-testid={`copy-${cast.id}`}>
                    {copiedId === cast.id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  {!cast.is_public && (
                    <button onClick={() => handlePublish(cast.id)}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-emerald-400 transition-colors" title="Publish to PI Store" data-testid={`publish-${cast.id}`}>
                      <Globe className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(cast.id)}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-${cast.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
